import { Link } from 'react-router-dom';
import { ArrowRight, MapPin, Clock, Building2, Briefcase, Store, Check, Zap, Factory, Users, Wrench, Home } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { SEO } from '../../components/SEO';

export default function AinPage() {
  return (
    <main data-testid="seo-page-ain" className="pt-32">
      <SEO 
        title="Création de site web dans l'Ain (01) | Bourg-en-Bresse, Oyonnax, Ambérieu, Valserhône"
        description="Webdesigner freelance basé dans l'Ain. Création de sites web pour PME, artisans et indépendants à Bourg-en-Bresse, Oyonnax, Ambérieu-en-Bugey. De la Plastics Vallée au Bugey. Devis gratuit sous 48h."
        path="/creation-site-web-ain"
      />

      {/* Hero Section */}
      <section className="container-main mb-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="animate-fade-up">
            <div className="flex items-center gap-3 mb-4">
              <img 
                src="/images/seo/ain.png" 
                alt="Ain" 
                className="w-10 h-10 object-contain"
              />
              <span className="text-sm uppercase tracking-widest font-bold text-primary">
                Département de l'Ain (01)
              </span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight leading-tight mb-6">
              Création de site web
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-teal-600 pb-2">
                dans l'Ain
              </span>
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground leading-relaxed mb-8">
              Webdesigner freelance basé dans l'Ain, j'accompagne les <strong>PME, artisans et indépendants</strong> du département. De <strong>Bourg-en-Bresse</strong> à <strong>Oyonnax</strong>, de la Plastics Vallée au Bugey, je connais votre territoire.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/tarifs#contact">
                <Button className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white text-lg px-8 py-6">
                  Discuter de mon projet
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/tarifs">
                <Button variant="outline" className="text-lg px-8 py-6">
                  Voir les tarifs
                </Button>
              </Link>
            </div>
          </div>

          {/* Image Ain / Bugey */}
          <div className="animate-fade-up animation-delay-200">
            <img 
              src="/images/seo/ain-nature.jpg"
              alt="Création site web Ain - La rivière d'Ain"
              className="w-full h-auto rounded-2xl object-cover shadow-2xl"
            />
            <p className="text-xs text-muted-foreground mt-2 text-right">La rivière d'Ain</p>
          </div>
        </div>
      </section>

      {/* Section: Ancré dans l'Ain */}
      <section className="bg-muted/30 section-padding">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Stats Card */}
            <div className="order-2 lg:order-1">
              <Card className="overflow-hidden border-2 bg-gradient-to-br from-emerald-600 to-teal-600 text-white">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold mb-6">L'Ain en chiffres</h3>
                  <div className="grid grid-cols-2 gap-6">
                    <div className="text-center p-4 bg-white/10 rounded-xl">
                      <div className="text-3xl font-black">650 000</div>
                      <div className="text-sm opacity-80">habitants</div>
                    </div>
                    <div className="text-center p-4 bg-white/10 rounded-xl">
                      <div className="text-3xl font-black">419</div>
                      <div className="text-sm opacity-80">communes</div>
                    </div>
                    <div className="text-center p-4 bg-white/10 rounded-xl">
                      <div className="text-3xl font-black">1er</div>
                      <div className="text-sm opacity-80">pôle plasturgie FR</div>
                    </div>
                    <div className="text-center p-4 bg-white/10 rounded-xl">
                      <div className="text-3xl font-black">Lyon</div>
                      <div className="text-sm opacity-80">à 1h de route</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
            
            <div className="order-1 lg:order-2">
              <div className="inline-flex items-center gap-2 bg-emerald-100 dark:bg-emerald-950/50 text-emerald-700 dark:text-emerald-400 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Home className="h-4 w-4" />
                Basé dans l'Ain
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Webdesigner freelance<br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-teal-600">
                  dans l'Ain (01)
                </span>
              </h2>
              <p className="text-lg text-muted-foreground mb-6 leading-relaxed">
                L'Ain, c'est <strong>650 000 habitants</strong>, des paysages variés et une économie diversifiée. 
                De l'industrie plasturgique d'<strong>Oyonnax</strong> aux commerces de <strong>Bourg-en-Bresse</strong>, chaque entreprise mérite une présence web à la hauteur.
              </p>
              <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
                Basé dans le département, je me déplace à <strong>Ambérieu-en-Bugey</strong>, <strong>Valserhône</strong> et dans tout l'Ain. 
                Création de site web sur-mesure pour votre activité.
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-3 p-4 bg-white dark:bg-card rounded-xl border">
                  <MapPin className="h-8 w-8 text-emerald-600 shrink-0" />
                  <div>
                    <div className="font-bold">Local</div>
                    <div className="text-sm text-muted-foreground">Je me déplace</div>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-4 bg-white dark:bg-card rounded-xl border">
                  <Users className="h-8 w-8 text-emerald-600 shrink-0" />
                  <div>
                    <div className="font-bold">Direct</div>
                    <div className="text-sm text-muted-foreground">Un seul interlocuteur</div>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-4 bg-white dark:bg-card rounded-xl border">
                  <Zap className="h-8 w-8 text-emerald-600 shrink-0" />
                  <div>
                    <div className="font-bold">Réactif</div>
                    <div className="text-sm text-muted-foreground">Réponse sous 48h</div>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-4 bg-white dark:bg-card rounded-xl border">
                  <Wrench className="h-8 w-8 text-emerald-600 shrink-0" />
                  <div>
                    <div className="font-bold">Sur-mesure</div>
                    <div className="text-sm text-muted-foreground">Adapté à vos besoins</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section: Pour qui */}
      <section className="section-padding">
        <div className="container-main">
          <div className="text-center mb-12">
            <span className="text-sm uppercase tracking-widest font-bold text-primary mb-4 block">
              Pour qui ?
            </span>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Création de site web pour les professionnels<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-teal-600">
                de l'Ain
              </span>
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              PME industrielles, artisans, commerçants, indépendants à Bourg-en-Bresse, Oyonnax, Ambérieu... Un site web adapté à votre activité.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Card: PME / Industrie */}
            <Card className="group hover:border-emerald-500/50 transition-all hover:shadow-lg border-2">
              <CardContent className="p-8">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-white mb-6 group-hover:scale-110 transition-transform">
                  <Factory className="h-8 w-8" />
                </div>
                <h3 className="text-xl font-bold mb-3">PME & Industrie</h3>
                <p className="text-muted-foreground mb-4">
                  Plasturgie, mécanique, agroalimentaire... Un site vitrine professionnel pour valoriser votre savoir-faire.
                </p>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-green-500" />
                    Présentation métiers
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-green-500" />
                    Catalogue produits
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-green-500" />
                    Formulaire de contact pro
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Card: Indépendants */}
            <Card className="group hover:border-emerald-500/50 transition-all hover:shadow-lg border-2">
              <CardContent className="p-8">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white mb-6 group-hover:scale-110 transition-transform">
                  <Briefcase className="h-8 w-8" />
                </div>
                <h3 className="text-xl font-bold mb-3">Indépendants</h3>
                <p className="text-muted-foreground mb-4">
                  Consultants, professions libérales, coachs... Un site personnel qui génère des contacts qualifiés.
                </p>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-green-500" />
                    Personal branding
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-green-500" />
                    Prise de RDV en ligne
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-green-500" />
                    Optimisé SEO local
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Card: Artisans & Commerces */}
            <Card className="group hover:border-emerald-500/50 transition-all hover:shadow-lg border-2">
              <CardContent className="p-8">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-orange-500 to-amber-600 flex items-center justify-center text-white mb-6 group-hover:scale-110 transition-transform">
                  <Store className="h-8 w-8" />
                </div>
                <h3 className="text-xl font-bold mb-3">Artisans & Commerces</h3>
                <p className="text-muted-foreground mb-4">
                  Boulangeries, restaurants, garages, coiffeurs... Une vitrine en ligne pour attirer la clientèle locale.
                </p>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-green-500" />
                    Fiche Google optimisée
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-green-500" />
                    Photos & galerie
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-green-500" />
                    Horaires & contact
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Section: Villes couvertes */}
      <section className="bg-gradient-to-br from-slate-900 to-slate-800 text-white section-padding">
        <div className="container-main">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Création de site web dans tout l'Ain (01)
            </h2>
            <p className="text-slate-300 max-w-2xl mx-auto">
              Bourg-en-Bresse, Oyonnax, Ambérieu-en-Bugey, Valserhône, Villars-les-Dombes... Je me déplace pour vous rencontrer.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {[
              { name: 'Bourg-en-Bresse', desc: 'Préfecture, services', Icon: Building2 },
              { name: 'Oyonnax', desc: 'Plastics Vallée', Icon: Factory },
              { name: 'Ambérieu-en-Bugey', desc: 'Carrefour ferroviaire', Icon: Building2 },
              { name: 'Villars-les-Dombes', desc: 'Parc des oiseaux', Icon: MapPin },
              { name: 'Valserhône', desc: 'Pays de Gex', Icon: MapPin },
            ].map((zone) => (
              <div key={zone.name} className="text-center p-6 bg-white/5 rounded-xl hover:bg-white/10 transition-colors">
                <div className="w-12 h-12 mx-auto rounded-full bg-gradient-to-br from-emerald-600 to-teal-600 flex items-center justify-center mb-3">
                  <zone.Icon className="h-6 w-6 text-white" />
                </div>
                <h3 className="font-bold text-white text-sm">{zone.name}</h3>
                <p className="text-xs text-slate-400 mt-1">{zone.desc}</p>
              </div>
            ))}
          </div>

          {/* Zones géographiques */}
          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-6 bg-white/5 rounded-xl">
              <div className="text-xl font-bold text-emerald-400">Bresse</div>
              <div className="text-sm text-slate-400">Agriculture & gastronomie</div>
            </div>
            <div className="text-center p-6 bg-white/5 rounded-xl">
              <div className="text-xl font-bold text-emerald-400">Bugey</div>
              <div className="text-sm text-slate-400">Tourisme & nature</div>
            </div>
            <div className="text-center p-6 bg-white/5 rounded-xl">
              <div className="text-xl font-bold text-emerald-400">Dombes</div>
              <div className="text-sm text-slate-400">Étangs & patrimoine</div>
            </div>
            <div className="text-center p-6 bg-white/5 rounded-xl">
              <div className="text-xl font-bold text-emerald-400">Pays de Gex</div>
              <div className="text-sm text-slate-400">Frontaliers & dynamisme</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="section-padding bg-muted/30">
        <div className="container-main">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Votre site web dans l'Ain
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              Création de site internet à Bourg-en-Bresse, Oyonnax, Ambérieu-en-Bugey ou ailleurs dans l'Ain. Devis gratuit sous 48h.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <Link to="/tarifs#contact">
                <Button className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white text-lg px-8 py-6 w-full sm:w-auto">
                  Demander un devis gratuit
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/processus">
                <Button variant="outline" className="text-lg px-8 py-6 w-full sm:w-auto">
                  Découvrir mon processus
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>

            <div className="flex items-center justify-center gap-6 text-muted-foreground">
              <div className="flex items-center gap-2">
                <MapPin className="h-5 w-5" />
                <span>Ain (01)</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-5 w-5" />
                <span>Devis sous 48h</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
